import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Card, Typography, type TypographyProps } from '@heroui/react';
import { PressableFeedback } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UseWelcomeStepResult } from './useWelcomeStep';

type WelcomeStepViewProps = UseWelcomeStepResult;

// The greeting is the step's only heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

type ChoiceCardProps = {
    icon: string;
    title: string;
    description: string;
    onPress: () => void;
    testId: string;
};

/**
 * A choice card is a native button drawn as a card, so Tab reaches it and
 * Enter or Space activates it. A button holds phrasing content only, hence
 * spans for the title and the description.
 */
const ChoiceCard: React.FC<ChoiceCardProps> = ({
    icon,
    title,
    description,
    onPress,
    testId,
}) => (
    <Card<'button'>
        className="relative w-full cursor-pointer items-start gap-4 overflow-hidden p-6 text-start"
        onClick={onPress}
        render={(props) => <button type="button" {...props} />}
        {...getNodeTestIdProp(testId)}
    >
        <PressableFeedback.Highlight />
        <Icon aria-hidden className="size-8 text-accent" icon={icon} />
        <Card.Header className="gap-2">
            <Card.Title render={(props) => <span {...props} />}>
                {title}
            </Card.Title>
            <Card.Description render={(props) => <span {...props} />}>
                {description}
            </Card.Description>
        </Card.Header>
    </Card>
);

/**
 * Welcome: a greeting to the organisation, one sentence, and the two paths.
 * Start fresh finishes with nothing imported; Bring my catalog starts the
 * import.
 */
export const WelcomeStepView: React.FC<WelcomeStepViewProps> = ({
    organisationName,
    onStartFresh,
    onBringCatalog,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            className="flex flex-col gap-8 py-4 sm:py-8"
            {...getNodeTestIdProp('onboarding-welcome')}
        >
            <div className="flex flex-col gap-2">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('onboarding-welcome-title')}
                >
                    {t('onboarding.welcome.title', {
                        organisation: organisationName,
                    })}
                </Typography>
                <Typography.Paragraph
                    color="muted"
                    {...getNodeTestIdProp('onboarding-welcome-intro')}
                >
                    {t('onboarding.welcome.intro')}
                </Typography.Paragraph>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ChoiceCard
                    description={t('onboarding.welcome.startFresh.description')}
                    icon="gravity-ui:rocket"
                    onPress={onStartFresh}
                    testId="onboarding-welcome-start-fresh"
                    title={t('onboarding.welcome.startFresh.title')}
                />
                <ChoiceCard
                    description={t(
                        'onboarding.welcome.bringCatalog.description',
                    )}
                    icon="gravity-ui:file-arrow-up"
                    onPress={onBringCatalog}
                    testId="onboarding-welcome-bring-catalog"
                    title={t('onboarding.welcome.bringCatalog.title')}
                />
            </div>
        </section>
    );
};
